import React, { useState, useEffect } from 'react';
import { recordFeePayment } from '../firebase/database';
import { getCurrentUser } from '../firebase/auth';
import './PaymentForm.css';

function PaymentForm({ tenantId, member, onClose, onSuccess }) {
  const [formData, setFormData] = useState({
    amount: '',
    paymentMethod: 'cash',
    notes: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    if (member && member.fees) {
      // Extract numeric value from fees string (e.g., "₹2000" -> 2000)
      const numericValue = parseFloat(member.fees.replace(/[^\d.]/g, ''));
      if (!isNaN(numericValue)) {
        setFormData(prev => ({
          ...prev,
          amount: numericValue.toString()
        }));
      }
    }
  }, [member]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.amount || parseFloat(formData.amount) <= 0) {
      setError('Please enter a valid payment amount');
      return;
    }

    setIsSubmitting(true);
    setError('');
    setSuccess('');

    try {
      const paymentData = {
        memberId: member.id,
        memberName: member.fullName,
        amount: parseFloat(formData.amount),
        paymentMethod: formData.paymentMethod,
        notes: formData.notes
      };

      console.log('Recording payment:', paymentData);
      console.log('Member ID:', member.id);
      console.log('Member data:', member);
      
      // Check authentication status
      const currentUser = getCurrentUser();
      console.log('Current user:', currentUser);
      
      if (!currentUser) {
        setError('You must be logged in to record payments');
        return;
      }
      
      console.log('User email:', currentUser.email);
      console.log('User UID:', currentUser.uid);
      
      const result = await recordFeePayment(tenantId, member.id, paymentData);
      console.log('Payment result:', result);
      
      if (result.success) {
        setSuccess('Payment recorded successfully!');
        setTimeout(() => {
          onSuccess();
          onClose();
        }, 1500);
      } else {
        console.error('Payment failed:', result);
        setError(result.message || 'Failed to record payment');
      }
    } catch (error) {
      console.error('Payment recording error:', error);
      setError(`An error occurred: ${error.message}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  const getDaysUntilDue = () => {
    if (!member.nextDueDate) return null;
    
    const dueDate = new Date(member.nextDueDate);
    const today = new Date();
    const diffTime = dueDate - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    return diffDays;
  };

  const getPaymentStatus = () => {
    if (!member.nextDueDate) return 'No due date set';
    
    const daysUntilDue = getDaysUntilDue();
    
    if (daysUntilDue < 0) {
      return 'Overdue';
    } else {
      return 'Due';
    }
  };

  const getStatusColor = () => {
    const daysUntilDue = getDaysUntilDue();
    
    if (daysUntilDue < 0) return '#e53e3e'; // Red for overdue
    return '#d69e2e'; // Yellow for due
  };

  return (
    <div className="payment-form-overlay" onClick={onClose}>
      <div className="payment-form-modal" onClick={(e) => e.stopPropagation()}>
        <div className="payment-form-header">
          <h2>Record Payment</h2>
          <button className="close-btn" onClick={onClose}>×</button>
        </div>

        <div className="payment-form-content">
          {/* Member Information */}
          <div className="member-info-section">
            <div className="member-avatar">
              {member.fullName ? member.fullName.charAt(0).toUpperCase() : '?'}
            </div>
            <div className="member-details">
              <h3>{member.fullName || 'Unknown Member'}</h3>
              <p className="member-id">ID: {member.membershipId || member.id}</p>
              <p className="membership-info">
                {member.membershipLevel} • {member.membershipDuration} • {member.sex ? member.sex.charAt(0).toUpperCase() + member.sex.slice(1) : 'N/A'}
              </p>
            </div>
          </div>

          {/* Payment Status */}
          <div className="payment-status-section">
            <div className="status-indicator" style={{ backgroundColor: getStatusColor() }}>
              <span className="status-text">{getPaymentStatus()}</span>
            </div>
            {member.lastPaymentDate && (
              <p className="last-payment">
                Last Payment: {new Date(member.lastPaymentDate).toLocaleDateString()}
              </p>
            )}
            {member.nextDueDate && (
              <p className="next-due">
                Next Due: {new Date(member.nextDueDate).toLocaleDateString()}
              </p>
            )}
          </div>

          {/* Payment Form */}
          <form onSubmit={handleSubmit} className="payment-form">
            <div className="form-group">
              <label htmlFor="amount">Payment Amount (₹)</label>
              <input
                type="number"
                id="amount"
                name="amount"
                value={formData.amount}
                onChange={handleInputChange}
                placeholder="Enter amount"
                step="0.01"
                min="0"
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="paymentMethod">Payment Method</label>
              <select
                id="paymentMethod"
                name="paymentMethod"
                value={formData.paymentMethod}
                onChange={handleInputChange}
                required
              >
                <option value="cash">Cash</option>
                <option value="online">Online Transfer</option>
                <option value="card">Card Payment</option>
                <option value="upi">UPI</option>
                <option value="other">Other</option>
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="notes">Notes (Optional)</label>
              <textarea
                id="notes"
                name="notes"
                value={formData.notes}
                onChange={handleInputChange}
                placeholder="Add any additional notes about this payment..."
                rows="3"
              />
            </div>

            {error && <div className="error-message">{error}</div>}
            {success && <div className="success-message">{success}</div>}

            <div className="form-actions">
              <button
                type="button"
                className="cancel-btn"
                onClick={onClose}
                disabled={isSubmitting}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="submit-btn"
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Recording...' : 'Record Payment'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

export default PaymentForm; 